class LogoCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = [];
    this.pagination = null;
    this.currentIndex = 0;
    this.isSliderMode = false;
    this.slideWidth = 0;
    this.gap = 40;
    this.totalOriginalSlides = 0;
    this.resumeTimeout = null;
  }

  connectedCallback() {
    this.track = this.querySelector('.logo-carousel__track');
    this.slides = Array.from(this.querySelectorAll('.logo-carousel__slide'));
    this.pagination = this.querySelector('.logo-carousel__pagination');
    this.prevButton = this.querySelector('.logo-carousel__arrow.--prev');
    this.nextButton = this.querySelector('.logo-carousel__arrow.--next');

    if (!this.track || this.slides.length === 0) return;

    // Les slides sont dupliquées 4x dans le HTML, donc on divise par 4
    this.totalOriginalSlides = this.slides.length / 4;

    this.#createPagination();
    this.#bindEvents();
  }

  #createPagination() {
    if (!this.pagination) return;

    for (let i = 0; i < this.totalOriginalSlides; i++) {
      const bullet = document.createElement('button');
      bullet.className = 'logo-carousel__pagination-bullet';
      if (i === 0) bullet.classList.add('--active');
      bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);
      bullet.addEventListener('click', () => this.#goToSlide(i));
      this.pagination.appendChild(bullet);
    }
  }

  #bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.#navigate('prev'));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.#navigate('next'));
    }
  }

  #enableSliderMode() {
    if (this.isSliderMode) return;

    this.isSliderMode = true;
    this.classList.add('--slider-mode');

    // Calculer la largeur d'un groupe de slides (25% du track car 4 duplications)
    const trackWidth = this.track.scrollWidth;
    this.groupWidth = trackWidth / 4;

    // Obtenir la position actuelle de l'animation
    const computedStyle = window.getComputedStyle(this.track);
    const matrix = new DOMMatrix(computedStyle.transform);
    const currentX = matrix.m41;

    // Calculer l'index actuel basé sur la position
    const progress = Math.abs(currentX) / this.groupWidth;
    this.currentIndex = Math.round(progress * this.totalOriginalSlides) % this.totalOriginalSlides;

    // Appliquer la position actuelle comme transform statique
    this.#updatePosition(false);
  }

  #disableSliderMode() {
    if (!this.isSliderMode) return;

    this.isSliderMode = false;
    this.classList.remove('--slider-mode');
    this.track.style.transform = '';
    this.currentIndex = 0;
    this.#updatePagination();
  }

  #navigate(direction) {
    this.#enableSliderMode();
    this.#clearResumeTimeout();

    if (direction === 'prev') {
      this.currentIndex = (this.currentIndex - 1 + this.totalOriginalSlides) % this.totalOriginalSlides;
    } else {
      this.currentIndex = (this.currentIndex + 1) % this.totalOriginalSlides;
    }

    this.#updatePosition(true);
    this.#updatePagination();
    this.#scheduleResume();
  }

  #goToSlide(index) {
    this.#enableSliderMode();
    this.#clearResumeTimeout();

    this.currentIndex = index;
    this.#updatePosition(true);
    this.#updatePagination();
    this.#scheduleResume();
  }

  #updatePosition(animate = true) {
    if (!this.isSliderMode) return;

    const slideElements = this.slides.slice(0, this.totalOriginalSlides);
    let offset = 0;

    for (let i = 0; i < this.currentIndex; i++) {
      offset += slideElements[i].offsetWidth + this.gap;
    }

    this.track.style.transition = animate ? 'transform 0.5s ease' : 'none';
    this.track.style.transform = `translateX(-${offset}px)`;
  }

  #updatePagination() {
    if (!this.pagination) return;

    const bullets = this.pagination.querySelectorAll('.logo-carousel__pagination-bullet');
    bullets.forEach((bullet, index) => {
      bullet.classList.toggle('--active', index === this.currentIndex);
    });
  }

  #scheduleResume() {
    this.#clearResumeTimeout();
    this.resumeTimeout = setTimeout(() => {
      this.#disableSliderMode();
    }, 5000);
  }

  #clearResumeTimeout() {
    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = null;
    }
  }
}

customElements.define('logo-carousel', LogoCarousel);
